import { getCurrentScope } from '@sentry/nextjs';
import { useSnapshot } from 'valtio';

import { getBrandConfig } from '@/lib/configuration/configuration';
import { userFetch } from '@/lib/user';
import type { UserState } from '@/states/user';
import { userActions, userState } from '@/states/user';

export const useUser = <Require extends boolean = false>() => {
  const snapshot = useSnapshot<UserState<Require>>(userState);

  const fetchUser = async (isEditMode = false) => {
    if (getBrandConfig().disableSaleorApiCall) return null;
    try {
      if (userState.user && !isEditMode) {
        userActions.setAuthenticated({ isAuthenticated: true });
        endProcessing({ initializing: false });
        return userState.user;
      }

      startProcessing({ initializing: true });

      const user = await userFetch(isEditMode);
      if (user) {
        getCurrentScope().setUser({ id: user.id });
        userActions.setUser(user);
        userActions.setAuthenticated({ isAuthenticated: true });
        endProcessing({ initializing: false });
        return user;
      }

      userActions.setAuthenticated({ isAuthenticated: false });
      endProcessing({ initializing: false });
      return null;
    } catch (error) {
      userActions.setAuthenticated({ isAuthenticated: false });
      endProcessing({ initializing: false });
      return null;
    }
  };

  const startProcessing = (
    opts?: Parameters<typeof userActions.updateProcessingState>[number],
  ) => {
    userActions.updateProcessingState({ processing: true, ...opts });
  };

  const endProcessing = (
    opts?: Parameters<typeof userActions.updateProcessingState>[number],
  ) => {
    userActions.updateProcessingState({
      processing: false,
      isInitialized: true,
      ...opts,
    });
  };

  return { snapshot, fetchUser };
};
